'use client'

import { useCallback, useRef, useState } from 'react'

/** The nearest ancestor that actually scrolls right now -- not merely one
 * styled overflow:auto (several wrappers are) but one whose scrollHeight
 * exceeds its clientHeight. In practice BackofficeApp's
 * <section overflow-auto h-screen>. Same walk as SibsMailView's. */
export function getScrollParent(start: HTMLElement | null): HTMLElement | null {
  let el = start?.parentElement ?? null
  while (el) {
    if (/(auto|scroll)/.test(getComputedStyle(el).overflowY) && el.scrollHeight > el.clientHeight) return el
    el = el.parentElement
  }
  return null
}

/** The "you were just here" marker on the row last opened -- the same blue
 * the SIBS mail dashboard keeps on its last-selected entry. */
export const LAST_OPENED_ROW_CLASS = 'bg-signal-surface ring-1 ring-inset ring-ring/40'

/**
 * What the SIBS mail dashboard does for its entries list (SibsMailView:
 * lastSelectedId / listScrollTopRef / restoreListScrollRef), for any
 * dashboard that swaps its list for a detail page. The list subtree
 * unmounts while the detail shows and the page's scrolling <section> gets
 * clamped by the shorter detail, so coming back landed at the top with no
 * clue which row had been opened (2026-09-20). Now:
 *   - `open(id, rowEl, go)` on the row click: remembers the scroll offset
 *     (read while the list DOM is still there) and the row, then calls go().
 *   - `restoreRef` on the list root (BaseDashboard's rootRef): when the
 *     list remounts, puts the scroll back -- deferred two frames because
 *     widgets and charts measure themselves after mount, so an immediate
 *     scrollTop gets clamped -- and, if the row is there but out of view
 *     (the list changed underneath), brings it in. Hidden until then so
 *     the top-of-page position never paints.
 *   - `isLast(id)` for the highlight (LAST_OPENED_ROW_CLASS) and the
 *     data-last-opened attribute the restore looks for.
 */
export function useListReturn() {
  const [lastOpenedId, setLastOpenedId] = useState<string | null>(null)
  const scrollTopRef = useRef(0)
  const lastIdRef = useRef<string | null>(null)

  const open = useCallback((id: string, rowEl: HTMLElement | null, go: () => void) => {
    const sp = getScrollParent(rowEl)
    scrollTopRef.current = sp ? sp.scrollTop : 0
    lastIdRef.current = id
    setLastOpenedId(id)
    go()
  }, [])

  const restoreRef = useCallback((el: HTMLDivElement | null) => {
    if (!el || lastIdRef.current == null) return
    const top = scrollTopRef.current
    el.style.visibility = 'hidden'
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const sp = getScrollParent(el)
        if (sp) sp.scrollTop = top
        const row = el.querySelector<HTMLElement>('[data-last-opened="true"]')
        if (row && sp) {
          const r = row.getBoundingClientRect()
          const s = sp.getBoundingClientRect()
          if (r.top < s.top || r.bottom > s.bottom) row.scrollIntoView({ block: 'center' })
        }
        el.style.visibility = 'visible'
      })
    })
  }, [])

  const isLast = useCallback((id: string) => id === lastOpenedId, [lastOpenedId])
  return { lastOpenedId, open, restoreRef, isLast }
}
